/**
 * Connectivity Tests — Ping, ASTM handshake, latency, throughput
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Activity, Webhook, Network, Play, CheckCircle2, XCircle, Loader2, Zap, Gauge, History } from "lucide-react";
import { getMockProfiles } from "@/lib/device/profiles";

export const Route = createFileRoute("/integration/connectivity-tests")({
  component: ConnectivityTests,
});

interface TestResult {
  deviceId: string;
  test: "ping" | "astm-handshake" | "hl7-ack" | "throughput";
  result: "PASS" | "FAIL" | "WARNING";
  latencyMs: number;
  detail: string;
  at: string;
}

function ConnectivityTests() {
  const [results, setResults] = useState<TestResult[]>([]);
  const [runningTest, setRunningTest] = useState<string | null>(null);

  const profilesQ = useQuery({
    queryKey: ["device-profiles"],
    queryFn: async () => {
      await new Promise((r) => setTimeout(r, 150));
      return getMockProfiles();
    },
  });

  function runTest(profileId: string, test: TestResult["test"]) {
    const key = `${profileId}-${test}`;
    setRunningTest(key);
    setTimeout(() => {
      const ok = Math.random() > 0.15;
      const latency = 12 + Math.floor(Math.random() * 200);
      const detail =
        test === "ping" ? (ok ? "4 packets transmitted, 4 received" : "Connection timed out") :
        test === "astm-handshake" ? (ok ? "ENQ/ACK/EOT cycle OK" : "ENQ/ACK timeout") :
        test === "hl7-ack" ? (ok ? "MSH/ACK validated" : "MSA-1 = AE (error)") :
        (ok ? `${100 + Math.floor(Math.random() * 500)} msg/s` : "Throughput too low");
      setResults((prev) => [{
        deviceId: profileId,
        test,
        result: ok ? (latency > 150 ? "WARNING" : "PASS") : "FAIL",
        latencyMs: latency,
        detail,
        at: new Date().toISOString(),
      }, ...prev].slice(0, 100));
      setRunningTest(null);
    }, 1200);
  }

  const profiles = profilesQ.data ?? [];

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
          <Webhook className="h-5 w-5" /> Connectivity Tests
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Ping · ASTM handshake · HL7 ACK · Throughput · Logs de session
        </p>
      </div>

      <Tabs defaultValue="ping">
        <TabsList>
          <TabsTrigger value="ping"><Activity className="h-3.5 w-3.5 mr-1" /> Ping</TabsTrigger>
          <TabsTrigger value="astm"><Network className="h-3.5 w-3.5 mr-1" /> ASTM</TabsTrigger>
          <TabsTrigger value="hl7"><Zap className="h-3.5 w-3.5 mr-1" /> HL7</TabsTrigger>
          <TabsTrigger value="throughput"><Gauge className="h-3.5 w-3.5 mr-1" /> Throughput</TabsTrigger>
          <TabsTrigger value="logs"><History className="h-3.5 w-3.5 mr-1" /> Historique</TabsTrigger>
        </TabsList>

        {(["ping", "astm-handshake", "hl7-ack", "throughput"] as const).map((testKind) => (
          <TabsContent key={testKind} value={testKind === "astm-handshake" ? "astm" : testKind === "hl7-ack" ? "hl7" : testKind === "throughput" ? "throughput" : "ping"}>
            <Card>
              <CardHeader>
                <CardTitle>
                  {testKind === "ping" && "Test de connectivité réseau"}
                  {testKind === "astm-handshake" && "Handshake ASTM E1394"}
                  {testKind === "hl7-ack" && "Accusé de réception HL7"}
                  {testKind === "throughput" && "Débit (messages/seconde)"}
                </CardTitle>
                <CardDescription>
                  Lancez le test sur un ou plusieurs profils. Les résultats sont journalisés dans l'audit WORM.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-1.5">
                  {profilesQ.isLoading ? (
                    <Skeleton className="h-32 w-full" />
                  ) : (
                    profiles.map((p) => {
                      const key = `${p.id}-${testKind}`;
                      const last = results.find((r) => r.deviceId === p.id && r.test === testKind);
                      return (
                        <div key={p.id} className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] p-2 text-sm">
                          <Network className="h-3.5 w-3.5 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium truncate">{p.vendor} {p.model}</div>
                            <div className="text-[11px] text-muted-foreground font-mono">{p.host}:{p.port}</div>
                          </div>
                          {last && (
                            <div className="flex items-center gap-1 text-xs">
                              {last.result === "PASS" ? <CheckCircle2 className="h-3 w-3 text-emerald-400" /> :
                                last.result === "WARNING" ? <CheckCircle2 className="h-3 w-3 text-amber-400" /> :
                                  <XCircle className="h-3 w-3 text-red-400" />}
                              <span>{last.latencyMs}ms</span>
                            </div>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => runTest(p.id, testKind)}
                            disabled={runningTest === key}
                          >
                            {runningTest === key ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Play className="h-3 w-3" />
                            )}
                            <span className="ml-1">Lancer</span>
                          </Button>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}

        <TabsContent value="logs">
          <Card>
            <CardHeader>
              <CardTitle>Historique des tests</CardTitle>
              <CardDescription>{results.length} résultats · audit WORM</CardDescription>
            </CardHeader>
            <CardContent>
              {results.length === 0 ? (
                <div className="rounded-lg border border-dashed border-white/10 p-6 text-center text-sm text-muted-foreground">
                  Aucun test exécuté
                </div>
              ) : (
                <ul className="space-y-1">
                  {results.map((r, i) => {
                    const profile = profiles.find((p) => p.id === r.deviceId);
                    return (
                      <li key={i} className="flex items-center gap-2 rounded-md border border-white/5 bg-white/[0.02] p-2 text-xs">
                        {r.result === "PASS" ? <CheckCircle2 className="h-3 w-3 text-emerald-400" /> :
                          r.result === "WARNING" ? <CheckCircle2 className="h-3 w-3 text-amber-400" /> :
                            <XCircle className="h-3 w-3 text-red-400" />}
                        <span className="font-mono text-muted-foreground w-32">
                          {new Date(r.at).toLocaleString("fr-FR")}
                        </span>
                        <Badge variant="outline" className="text-[9px]">{r.test}</Badge>
                        <span className="flex-1 truncate">{profile?.vendor} {profile?.model}</span>
                        <span className="text-muted-foreground">{r.detail}</span>
                        <span className="font-mono text-muted-foreground w-12 text-right">{r.latencyMs}ms</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
